import { Link, useParams } from 'react-router-dom';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useConcelhos, useDistritos } from '../hooks/useConcelhos';
import JsonLd from '../components/JsonLd';

const SITE = 'https://tutela.land';

export default function DistritoHub() {
  const { slug = '' } = useParams();
  const distritos = useDistritos().data?.distritos ?? [];
  const concelhos = useConcelhos().data?.concelhos ?? [];
  const distrito = distritos.find((d) => d.slug === slug);
  const members = concelhos
    .filter((c) => c.distrito_slug === slug)
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt'));

  const nome = distrito?.nome ?? '';
  useDocumentMeta(
    distrito
      ? `Risco de incêndio no distrito de ${nome} | Tutela IGNIS`
      : 'Risco por distrito | Tutela IGNIS',
    distrito
      ? `Previsão do risco de incêndio rural nos ${distrito.n_concelhos} concelhos do distrito de ${nome}, quadrícula a quadrícula (1 km). Complemento ao IPMA.`
      : undefined,
    `/ignis/risco/distrito/${slug}`,
  );

  if (!distrito) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-ink-950 px-6 py-20 text-center text-mist">
        <h1 className="font-display text-2xl font-bold">Distrito não encontrado</h1>
        <p className="mt-4 text-mist-muted">
          <Link to="/ignis/risco" className="text-ember hover:underline">
            Ver todos os distritos
          </Link>
        </p>
      </div>
    );
  }

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'IGNIS', item: `${SITE}/ignis` },
      { '@type': 'ListItem', position: 2, name: 'Risco por concelho', item: `${SITE}/ignis/risco` },
      { '@type': 'ListItem', position: 3, name: nome, item: `${SITE}/ignis/risco/distrito/${slug}` },
    ],
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
      <JsonLd id="breadcrumb" data={breadcrumb} />
      <div className="mx-auto max-w-3xl px-6 py-14">
        <nav className="font-mono text-xs text-mist-faint">
          <Link to="/ignis" className="hover:text-mist">IGNIS</Link>
          {' / '}
          <Link to="/ignis/risco" className="hover:text-mist">Risco</Link>
          {' / '}
          <span className="text-mist-muted">{nome}</span>
        </nav>

        <h1 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
          Risco de incêndio no distrito de {nome}
        </h1>
        <p className="mt-4 text-lg leading-relaxed text-mist-muted">
          Previsão do risco de incêndio rural nos {distrito.n_concelhos} concelhos do distrito de{' '}
          {nome}, quadrícula a quadrícula de 1 km. Escolha um concelho para ver a previsão e o padrão
          histórico de fogos.
        </p>

        <div className="mt-8 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {members.map((c) => (
            <Link
              key={c.slug}
              to={`/ignis/risco/${c.slug}`}
              className="rounded-lg border border-ink-700 px-3 py-2 text-sm text-mist-muted transition-colors hover:bg-ink-800 hover:text-mist"
            >
              {c.nome}
            </Link>
          ))}
        </div>

        <p className="mt-10 text-sm text-mist-muted">
          <Link to="/ignis/risco" className="text-ember hover:underline">
            ← Todos os distritos
          </Link>
        </p>
      </div>
    </div>
  );
}
